import React, { useEffect, useState } from 'react'
import {
  Button,
  Modal,
  FileDropzone,
  Container,
  useToaster,
  Spinner
} from 'react-angle-dashboard-components'
import { FieldError, useFormContext } from 'react-hook-form'
import { ModalFooter } from './ModalFooter'
import { read, utils } from 'xlsx'
import { AddOrEditMemberModal } from './AddOrEditMemberModal'
import { CensusTable } from './CensusTable'
import FooterNav from '../../FooterNav'
import CensusRiskModal from './CensusRiskModal'
import { IQuoteFormData } from '../../QuoteForm'
import {
  useAssociateCensus,
  usePollForCensusRiskAssessment,
  useTriggerCensusRiskAssessment
} from '../../../../hooks/quote.hooks'
import { ICensusFormMember } from './schemas'
import { mapCensusInfoToDTO } from '../../../../services/quote/mappers'
import { InvalidFeedback } from '../../style'
import { ReactComponent as ExclaimationImg } from '../../../../assets/exclaimationCircle.svg'
import { ReactComponent as TrashCan } from '../../trashCan.svg'
import moment from 'moment'

let continueCallback = (skipApi: boolean) => {}

const CensusInfo = () => {
  const { showError, showSuccess } = useToaster()
  const { mutateAsync: associateCensus } = useAssociateCensus()

  const [showAddMemberModal, setShowAddMemberModal] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showRiskModal, setShowRiskModal] = useState(false)

  const { setValue, watch, formState } = useFormContext<IQuoteFormData>()

  const errors = formState.errors.censusInfo

  const quoteId = watch('entryId')
  const currentRows = watch('censusInfo') ?? []

  const { mutateAsync: triggerRiskAssessment } =
    useTriggerCensusRiskAssessment()
  const [isPollingForRisk, setIsPollingForRisk] = useState(false)
  const { data: risk } = usePollForCensusRiskAssessment(quoteId)

  const [isLoading, setIsLoading] = useState(false)

  const [showClearAllModal, setShowClearAllModal] = useState(false)

  const [isCensusFileProcessing, setIsCensusFileProcessing] = useState(false)

  const [isFirstRender, setIsFirstRender] = useState(true)

  interface IExcelMember {
    'First Name*': string
    'Last Name*': string
    'Relationship* (Employee/Spouse/Child)': string
    'Gender* (Female/Male)': string
    'Date of Birth*\n(MM/DD/YYYY)': string
    'Date of Birth*\r\n(MM/DD/YYYY)': string // Thanks Bill Gates
    'Date of Birth* \u2028(MM/DD/YYYY)': string // ?????
    'Zip*': string
    'Tier*': string
    'Plan Name': string
  }

  useEffect(() => {
    if (risk && !risk.stillProcessing && (isPollingForRisk || isFirstRender)) {
      setIsFirstRender(false)
      // risk is done
      setIsLoading(false)
      switch (risk.result) {
        case 'accepted':
          // skip draft modal
          if (isPollingForRisk) {
            continueCallback(true)
          }
          return
        case 'declined':
          // show draft modal
          setIsPollingForRisk(false)
          setShowRiskModal(true)
          return
        case 'errored':
          setIsPollingForRisk(false)
          setShowRiskModal(false)
          showError(
            'There was an error assess your risk. Please try again or contact support'
          )
          return
      }
    } else if (risk?.status === 'in_progress') {
      setIsPollingForRisk(true)
      setShowRiskModal(true)
      setIsLoading(true)
    }
  }, [isFirstRender, isPollingForRisk, risk, showError])

  const [censusFile, setCensusFile] = useState<File | null>(null)

  const onCloseCensusModal = () => {
    setCensusFile(null)
    setShowUploadModal(false)
  }

  const onSaveCensusFile = async () => {
    const RANGE = 8

    if (censusFile) {
      setIsCensusFileProcessing(true)
      try {
        const file = await censusFile.arrayBuffer()
        const workbook = read(file)

        const worksheet = workbook.Sheets[workbook.SheetNames[0]]
        const data: IExcelMember[] = utils.sheet_to_json(worksheet, {
          // set first row of data
          range: RANGE,
          // convert cell values to strings
          raw: false
        })

        type OrNull<T> = { [K in keyof T]: T[K] | null }

        const mapExcelDataToCensusMember = (
          excelMember: IExcelMember,
          sheetRow: number
        ): OrNull<ICensusFormMember> => {
          function findValueByPrefix(object: any, prefix: string) {
            for (var property in object) {
              if (
                object.hasOwnProperty(property) &&
                property.toString().startsWith(prefix)
              ) {
                return object[property]
              }
            }
          }

          const checkForEmpty = (name: string, val: string): string => {
            if (!val) {
              throw new Error(
                `Error on line ${sheetRow}: ${name} cannot be empty`
              )
            }
            return val.trim()
          }

          const throwInvalidValError = (name: string, val: string) => {
            throw new Error(
              `Unexpected ${name.toLowerCase()} value: "${val}" on line ${sheetRow}`
            )
          }

          const mapGender = (val: string): 'male' | 'female' | null => {
            checkForEmpty('Gender', val)
            switch (val.trim().toLowerCase()) {
              case 'male':
              case 'm':
                return 'male'
              case 'female':
              case 'f':
                return 'female'
              default:
                throwInvalidValError('gender', val)
            }
            return null
          }

          const mapRelationship = (
            val: string
          ): 'employee' | 'spouse' | 'child' | null => {
            checkForEmpty('Relationship', val)
            switch (val.trim().toLowerCase()) {
              case 'employee':
              case 'subscriber':
              case 'e':
              case 'ee':
                return 'employee'
              case 'spouse':
              case 'dependent':
              case 'sp':
              case 'dp':
                return 'spouse'
              case 'child':
              case 'c':
              case 'ch':
                return 'child'
              default:
                throwInvalidValError('relationship', val)
            }
            return null
          }

          const mapTier = (
            val: string
          ):
            | 'single'
            | 'employee_spouse'
            | 'employee_children'
            | 'family'
            | null => {
            checkForEmpty('Tier', val)
            switch (val.trim().toLowerCase()) {
              case 'employee':
                return 'single'
              case 'employee+spouse':
                return 'employee_spouse'
              case 'employee+child(ren)':
                return 'employee_children'
              case 'employee+family':
                return 'family'
              default:
                throwInvalidValError('tier', val)
            }
            return null
          }

          const isDateValid = (date: string): string => {
            moment.parseTwoDigitYear = function (yearStr) {
              const currentYear = parseInt(moment().format('YY'))
              return (
                parseInt(yearStr) +
                (parseInt(yearStr) > currentYear ? 1900 : 2000)
              )
            }
            const allowedDateFormats = [
              'MM-DD-YYYY',
              'MM-DD-YY',
              'YYYY-MM-DD',
              'MM/DD/YYYY',
              'MM/DD/YY',
              'YYYY/MM/DD',
              'M-D-YYYY',
              'M-D-YY',
              'YYYY-M-D',
              'M/D/YYYY',
              'M/D/YY',
              'YYYY/M/D',
              'MM-D-YYYY',
              'MM-D-YY',
              'YYYY-MM-D',
              'MM/D/YYYY',
              'MM/D/YY',
              'YYYY/MM/D',
              'M-DD-YYYY',
              'M-DD-YY',
              'YYYY-M-DD',
              'M/DD/YYYY',
              'M/DD/YY',
              'YYYY/M/DD'
            ]

            const momentObj = moment(date.trim(), allowedDateFormats, true)
            if (!momentObj.isBefore()) {
              throw new Error(
                `Error on line ${sheetRow}: birth date ${date} is either formatted incorrectly or it is in the future. Formats include: MM-DD-YYYY, MM-DD-YY, and YYYY-MM-DD. Both dashes(-) and forward slash(/) are accepted`
              )
            }
            return momentObj.format('MM/DD/YYYY')
          }

          const newMember = {
            firstName: checkForEmpty('First Name', excelMember['First Name*']),
            lastName: checkForEmpty('Last Name', excelMember['Last Name*']),
            relationship: mapRelationship(
              excelMember['Relationship* (Employee/Spouse/Child)']
            ),
            gender: mapGender(excelMember['Gender* (Female/Male)']),
            dateOfBirth: checkForEmpty(
              'Date of Birth',
              isDateValid(findValueByPrefix(excelMember, 'Date of Birth'))
            ),
            zipCode: checkForEmpty('Zip Code', excelMember['Zip*']),
            familyTier: mapTier(excelMember['Tier*']),
            prevPlanName: excelMember['Plan Name'] ?? ''
          }

          if (newMember.zipCode.length !== 5) {
            throw new Error(
              `Zip Code must be 5 digits: error on line ${sheetRow}`
            )
          }

          return newMember
        }
        // save to form
        setValue('censusInfo', [
          ...currentRows,
          ...data.map(
            (d, i) =>
              mapExcelDataToCensusMember(d, i + RANGE + 2) as ICensusFormMember
          )
        ])
        setIsCensusFileProcessing(false)
        setShowUploadModal(false)
        setCensusFile(null)
        showSuccess('Census file processed successfully!')
      } catch (err: any) {
        setIsCensusFileProcessing(false)
        let errMessage = `There was an error processing your census.`
        if ('message' in err) {
          errMessage += ` ${err.message}`
        }
        showError(errMessage)
      }
    }
  }

  const onRiskSaveAsDraft = () => {
    continueCallback(true)
  }

  const onClearAllClick = () => {
    setShowClearAllModal(true)
  }

  const onContinue = async (callback: (skipApi: boolean) => void) => {
    if (quoteId) {
      try {
        setIsLoading(true)
        await associateCensus({
          quoteId: quoteId.toString(),
          data: mapCensusInfoToDTO(currentRows)
        })
        await triggerRiskAssessment(quoteId.toString())
        setShowRiskModal(true)
        setIsPollingForRisk(true)
        continueCallback = callback
      } catch (err) {
        console.error(err)
        setIsLoading(false)
        showError('There was a problem saving your census.')
      }
    }
  }

  return (
    <Container className="p-4">
      <Modal
        centered
        show={showClearAllModal}
        size="lg"
        style={{
          zIndex: 9000
        }}
        onHide={() => setShowClearAllModal(false)}
      >
        <Modal.Header
          style={{
            fontSize: 20,
            fontWeight: 500
          }}
          closeButton
        >
          Clear All Members
        </Modal.Header>
        <Modal.Body className="d-flex flex-column align-items-center">
          <ExclaimationImg
            fill="#EDCA60"
            style={{
              width: 36,
              height: 36,
              overflow: 'visible',
              marginBottom: 20
            }}
          />
          <p
            className="text-center"
            style={{
              fontSize: 20,
              fontWeight: 500
            }}
          >
            Are you sure you would like to clear all members added?
            <br />
            You will not be able to undo this action.
          </p>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <Button
            variant="outline-primary"
            onClick={() => setShowClearAllModal(false)}
          >
            Close
          </Button>
          <Button
            onClick={() => {
              setShowClearAllModal(false)
              setValue('censusInfo', [])
            }}
            variant="danger"
          >
            Clear All
          </Button>
        </Modal.Footer>
      </Modal>
      <AddOrEditMemberModal
        key={JSON.stringify(showAddMemberModal)}
        show={showAddMemberModal}
        setShow={setShowAddMemberModal}
      />
      <CensusRiskModal
        open={showRiskModal}
        isLoading={isPollingForRisk}
        onClose={() => setShowRiskModal(false)}
        onSaveAsDraft={onRiskSaveAsDraft}
      />
      <Modal
        centered
        show={showUploadModal}
        size="lg"
        onHide={onCloseCensusModal}
        backdrop="static"
        style={{
          zIndex: 9000
        }}
      >
        <Modal.Header className="border-bottom pb-2" closeButton>
          <h5>Census Upload</h5>
        </Modal.Header>
        <Modal.Body>
          <h5 className="pb-3">
            Please follow the{' '}
            <a
              style={{
                textDecoration: 'underline'
              }}
              rel="nofollow noreferrer"
              target="_blank"
              href={'/census_template.xlsx'}
              download
            >
              Census Template
            </a>{' '}
            to upload census of members
          </h5>
          <span>Upload File</span>
          <FileDropzone
            onFileLoaded={([e]: File[]) => {
              setCensusFile(e)
            }}
          />
        </Modal.Body>
        <ModalFooter>
          <Button
            className="py-2"
            variant="outline-primary"
            onClick={() => onCloseCensusModal()}
          >
            Cancel
          </Button>

          <Button
            className="py-2 ml-4 d-flex justify-content-center"
            onClick={() => onSaveCensusFile()}
            disabled={!censusFile || isCensusFileProcessing}
          >
            Save
            {isCensusFileProcessing && (
              <Spinner className="ml-2" animation="border" />
            )}
          </Button>
        </ModalFooter>
      </Modal>
      {errors && (
        <InvalidFeedback>
          {(errors as unknown as FieldError).message}
        </InvalidFeedback>
      )}
      {currentRows?.length ? (
        <div>
          <div className="d-flex justify-content-between pb-3">
            <div>
              <h6 className="text-secondary">Census Plan Info</h6>
              <h4 className="text-dark">Add Census Plan Info</h4>
            </div>
            <div>
              <Button
                className="mr-4 py-2"
                variant="outline-danger"
                onClick={onClearAllClick}
              >
                <TrashCan className="mr-2" style={{ marginBottom: 2 }} />
                Clear all
              </Button>
              <Button
                variant="outline-primary"
                onClick={() => setShowUploadModal(true)}
                className="mr-4 py-2"
              >
                Upload Census
              </Button>
              <Button
                onClick={() => setShowAddMemberModal(true)}
                className="py-2"
              >
                Add Members Manually
              </Button>
            </div>
          </div>
          <CensusTable rows={currentRows} />
        </div>
      ) : (
        <div className="text-center" style={{ paddingTop: 40 }}>
          <h5>You have not added any members yet</h5>
          <p className="text-secondary">Upload census file or add members</p>
          <div>
            <Button
              variant="outline-primary"
              onClick={() => setShowUploadModal(true)}
              className="mr-2 py-2"
            >
              Upload Census
            </Button>
            <Button
              onClick={() => setShowAddMemberModal(true)}
              className="ml-2 py-2"
            >
              Add Members Manually
            </Button>
          </div>
        </div>
      )}
      <FooterNav
        triggerPath="censusInfo"
        onContinue={onContinue}
        isLoading={isLoading}
      />
    </Container>
  )
}

export default CensusInfo
